/**
 * Bundle 163 - Advanced (collapsed by default) ad-hoc lookup.
 *
 * The ONLY typing surface on /admin/incidents. Visually
 * de-emphasised so admins reach for the tile-driven flow
 * first. Copy explicitly steers them there.
 */
import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ChevronDown, ChevronRight, Search } from 'lucide-react';
import api from '@/utils/api';
import { VerdictChip } from '@/components/VerdictChip';
import { extractErrorMessage } from '@/utils/errorMessage';
import { toast } from 'sonner';

const AdvancedDiagnosticLookup = () => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const lookup = async (e) => {
    e?.preventDefault?.();
    if (!email.trim()) return;
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.get(
        `/admin/users/by-email-diagnostic?email=${encodeURIComponent(email.trim())}`);
      setResult(data);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Diagnostic failed.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      className="border border-neutral-200 shadow-none"
      data-testid="advanced-diagnostic-lookup-card"
    >
      <CardContent className="p-3">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center justify-between text-left"
          data-testid="advanced-diagnostic-toggle"
        >
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">
              Advanced
            </div>
            <div className="text-sm text-neutral-700">
              Diagnose by email (fallback only)
            </div>
          </div>
          {open ? <ChevronDown className="h-4 w-4 text-neutral-500" />
            : <ChevronRight className="h-4 w-4 text-neutral-500" />}
        </button>
        {open ? (
          <div className="mt-3 space-y-3" data-testid="advanced-diagnostic-body">
            <p className="text-xs text-neutral-500">
              Only use this if the user is not visible in any tile above.
            </p>
            <form onSubmit={lookup} className="flex items-center gap-2">
              <Input
                type="email"
                placeholder="user@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="advanced-diagnostic-email-input"
                className="max-w-sm"
              />
              <Button
                type="submit"
                size="sm"
                disabled={busy || !email.trim()}
                data-testid="advanced-diagnostic-submit"
              >
                <Search className="h-3.5 w-3.5 mr-1" />
                Diagnose
              </Button>
            </form>
            {result ? (
              result.found ? (
                <div
                  className="rounded-md border border-neutral-200 bg-neutral-50 p-3 text-xs space-y-1"
                  data-testid="advanced-diagnostic-result"
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-primary-900">
                      {result.first_name} {result.last_name}
                    </span>
                    <span className="text-neutral-500">{result.email}</span>
                    <VerdictChip
                      verdict={result.verdict_v2 || 'LEGACY'}
                      testId="advanced-diagnostic-verdict-chip"
                    />
                  </div>
                  <div className="text-neutral-600">
                    Signup route: <span className="font-mono">{result.classified_signup_route}</span>
                    {' - '}Has DOB: {String(result.has_profile_dob)}
                    {' - '}Days since signup: {result.days_since_signup ?? '?'}
                  </div>
                  <div className="pt-1">
                    <a
                      className="text-secondary-700 hover:text-secondary-900 underline"
                      href={`/admin/users/${result.id}`}
                      data-testid="advanced-diagnostic-open-profile"
                    >
                      Open profile →
                    </a>
                  </div>
                </div>
              ) : (
                <div
                  className="text-xs text-neutral-500"
                  data-testid="advanced-diagnostic-not-found"
                >
                  No user found for that email.
                </div>
              )
            ) : null}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default AdvancedDiagnosticLookup;
